import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Calendar, Users, Heart, XCircle, Sparkles, ArrowRight } from "lucide-react";

const AstridHistory = () => {
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Astrid History: The Task App Yahoo Killed in 90 Days",
    "description": "The complete history of Astrid, from its 2008 launch to Yahoo's acquisition and abrupt shutdown in August 2013.",
    "author": {
      "@type": "Organization",
      "name": "Chill Study Work"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "datePublished": "2025-01-01",
    "dateModified": "2025-01-01",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://chillstudy.work/astrid/history/"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "Astrid",
      "applicationCategory": "ProductivityApplication",
      "operatingSystem": "iOS, Android, Web"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Astrid History: The Task App Yahoo Killed in 90 Days | CSW</title>
        <meta name="description" content="The complete history of Astrid, from its 2008 launch to Yahoo's acquisition and abrupt shutdown in August 2013." />
        <meta name="keywords" content="Astrid history, Astrid shutdown, Yahoo acquisition, task management app history, Tim Su, 6Wunderkinder" />
        <link rel="canonical" href="https://chillstudy.work/astrid/history/" />
        <meta property="og:title" content="Astrid History: The Task App Yahoo Killed in 90 Days" />
        <meta property="og:description" content="The complete history of Astrid, from its 2008 launch to Yahoo's shutdown in 2013." />
        <meta property="og:url" content="https://chillstudy.work/astrid/history/" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Breadcrumb */}
          <nav className="mb-8 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-muted-foreground">
              <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
              <li>/</li>
              <li><Link to="/astrid/" className="hover:text-primary transition-colors">Astrid Alternative</Link></li>
              <li>/</li>
              <li className="text-foreground">History</li>
            </ol>
          </nav>

          {/* Hero Section */}
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Astrid: The Beloved Task App Yahoo Killed in 90 Days
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              With its friendly octopus mascot and innovative social features, Astrid redefined what a to-do app could be. 
              Then Yahoo bought it and shut it down in just three months.
            </p>
          </header>

          {/* Timeline Section */}
          <section className="mb-16">
            <div className="space-y-8">
              {/* Origins */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold">Origins and Growth (2008-2013)</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Astrid was launched in 2008 by <a href="https://www.linkedin.com/in/timrsu/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Tim Su</a> and 
                  Jon Paris in San Francisco. What began as a simple personal task tracker quickly evolved into a beloved productivity 
                  assistant for over <strong className="text-foreground">4 million users</strong> by the early 2010s.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Astrid stood out with its quirky personality—a friendly octopus mascot that offered playful encouragements—and 
                  its innovative social features. Users could create to-do lists, set reminders, and most notably, 
                  <strong className="text-foreground"> share tasks</strong> with others via @mentions.
                </p>
              </article>

              {/* Why People Loved It */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-pink-500/10 rounded-lg">
                    <Heart className="w-6 h-6 text-pink-500" />
                  </div>
                  <h2 className="text-2xl font-bold">Why People Loved It</h2>
                </div>
                <div className="space-y-4 text-muted-foreground">
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Social Task Sharing</h3>
                    <p>Astrid was one of the first to-do apps that let you assign tasks to friends or co-workers. Many couples and families used it for shared grocery lists and chore tracking.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Cross-Platform Sync</h3>
                    <p>Available on iPhone, Android, and the web, Astrid synced everything in the cloud so your tasks were always accessible.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Playful Personality</h3>
                    <p>Unlike sterile productivity apps, Astrid had charm. Its octopus mascot and lighthearted approach made task management feel less like work.</p>
                  </div>
                </div>
              </article>

              {/* Investment and Growth */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-accent/10 rounded-lg">
                    <Calendar className="w-6 h-6 text-accent" />
                  </div>
                  <h2 className="text-2xl font-bold">Investment and Recognition</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  In 2011, Astrid went through the <a href="https://www.angelpad.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">AngelPad</a> startup 
                  accelerator and attracted investment from <a href="https://www.gv.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Ventures</a> after 
                  reaching 1.5 million downloads.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  By 2013, Astrid's user base had grown loyal and passionate. The app was among the top-rated task managers on both Android 
                  and iOS, beloved for hitting the sweet spot between simplicity and power.
                </p>
              </article>

              {/* Yahoo Acquisition */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-destructive/10 rounded-lg">
                    <XCircle className="w-6 h-6 text-destructive" />
                  </div>
                  <h2 className="text-2xl font-bold">The Yahoo Acquisition and Shutdown (2013)</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  In May 2013, <a href="https://techcrunch.com/2013/07/06/astrid-goes-dark-august-5-goodnight-sweet-squid/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Yahoo acquired Astrid</a> as 
                  part of CEO Marissa Mayer's wave of startup purchases. The announcement immediately worried Astrid's users—and their fears proved valid.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Just <strong className="text-foreground">three months later</strong>, Yahoo announced Astrid would "go dark" on 
                  <strong className="text-foreground"> August 5, 2013</strong>. Yahoo offered no detailed explanation for killing 
                  a product with 4 million devoted users.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  The developers provided an export tool and recommended alternatives like Wunderlist, Any.do, and Wrike—but there 
                  was no reprieve for Astrid itself.
                </p>
              </article>

              {/* Community Reaction */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold">Community Reaction and Legacy</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Astrid's shutdown hit its community hard. The app's Facebook page filled with comments lamenting its death and 
                  expressing anger at Yahoo for buying and killing a product people loved.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Many saw it as another example of a big company snuffing out an innovative tool without regard for its users. 
                  The team's fate within Yahoo remained mysterious—the company praised their "background in personalized mobile 
                  experiences" but never clarified what, if anything, came of it.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Astrid did have a lasting legacy: its source code was later forked by independent developers to create an 
                  open-source to-do app called <a href="https://tasks.org/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Tasks.org</a> in 
                  2014, keeping Astrid's spirit alive for those who missed it.
                </p>
              </article>
            </div>
          </section>

          {/* Sources */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Sources</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://en.wikipedia.org/wiki/Astrid_(application)" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Wikipedia</a> on 
                  Astrid application history and features.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://techcrunch.com/2013/07/06/astrid-goes-dark-august-5-goodnight-sweet-squid/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">TechCrunch</a> on 
                  Yahoo's acquisition and Astrid's shutdown announcement.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://www.reddit.com/r/androidapps/comments/1iximv/my_to_do_list_app_astrid_is_being_discontinued/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Reddit discussion</a> on 
                  community reactions to the shutdown.
                </span>
              </li>
            </ul>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 text-center border border-primary/20">
            <h2 className="text-2xl font-bold mb-4">Looking for an Astrid Alternative?</h2>
            <p className="text-muted-foreground mb-6">
              Discover how CSW combines task management with collaborative study sessions to help you stay productive.
            </p>
            <Link
              to="/astrid/"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
            >
              Explore CSW as Astrid Alternative
              <ArrowRight className="w-4 h-4" />
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AstridHistory;
